import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth, API } from "../App";
import Sidebar from "../components/Sidebar";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import {
  Building2,
  Search,
  Star,
  MapPin,
  Briefcase,
  Users,
  ChevronRight,
  Loader2
} from "lucide-react";

const CompanyDirectory = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    fetchCompanies();
  }, []);

  const fetchCompanies = async (q = "") => {
    setLoading(true);
    try {
      const token = localStorage.getItem("session_token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await fetch(`${API}/companies/search?q=${encodeURIComponent(q)}`, { credentials: "include", headers });
      if (res.ok) {
        setCompanies(await res.json());
      }
    } catch {
      toast.error("Failed to load companies");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchCompanies(searchQuery);
  };

  return (
    <div className="min-h-screen bg-obsidian text-white">
      <Sidebar />
      <main className="lg:ml-64 pt-16 lg:pt-0 min-h-screen">
        <div className="max-w-5xl mx-auto p-4 sm:p-6 lg:p-8 space-y-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-3" data-testid="companies-title">
              <Building2 className="w-7 h-7 text-crimson" />
              Company Directory
            </h1>
            <p className="text-gray-400 mt-1 text-sm">Explore companies, read reviews, and find your next employer</p>
          </div>

          {/* Search */}
          <form onSubmit={handleSearch} className="flex gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
              <input
                type="text"
                placeholder="Search companies..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full bg-obsidian-light border border-[#333] rounded pl-10 pr-4 py-2.5 text-sm text-white placeholder-gray-500 focus:border-crimson focus:outline-none"
                data-testid="search-companies"
              />
            </div>
            <Button type="submit" className="bg-crimson hover:bg-crimson/90 text-white" data-testid="search-btn">
              Search
            </Button>
          </form>

          {/* Company Cards */}
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-crimson" />
            </div>
          ) : companies.length === 0 ? (
            <div className="text-center py-20 text-gray-500">
              <Building2 className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>No companies found</p>
              <p className="text-xs text-gray-600 mt-1">Companies appear here when recruiters create profiles</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {companies.map((c, idx) => (
                <motion.div
                  key={c.company_name}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.05 }}
                >
                  <Link
                    to={`/company/${encodeURIComponent(c.company_name)}`}
                    className="block bg-obsidian-light border border-[#222] hover:border-[#444] rounded-lg p-5 transition-colors group"
                    data-testid={`company-card-${idx}`}
                  >
                    <div className="flex items-start gap-4">
                      {c.company_logo ? (
                        <img src={c.company_logo} alt={c.company_name} className="w-12 h-12 rounded-lg object-cover flex-shrink-0 border border-[#333]" />
                      ) : (
                        <div className="w-12 h-12 bg-crimson/10 border border-crimson/20 rounded-lg flex items-center justify-center flex-shrink-0">
                          <Building2 className="w-6 h-6 text-crimson" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold group-hover:text-crimson transition-colors">{c.company_name}</h3>
                        <div className="flex flex-wrap items-center gap-3 mt-1 text-xs text-gray-500">
                          {c.industry && <span className="flex items-center gap-1"><Briefcase className="w-3 h-3" />{c.industry}</span>}
                          {c.location && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{c.location}</span>}
                        </div>
                        <div className="flex items-center gap-4 mt-3 text-xs">
                          <span className="flex items-center gap-1">
                            <Star className={`w-3.5 h-3.5 ${c.avg_rating > 0 ? "text-amber-400 fill-amber-400" : "text-gray-600"}`} />
                            <span className={c.avg_rating > 0 ? "text-amber-400 font-medium" : "text-gray-500"}>{c.avg_rating > 0 ? c.avg_rating : "N/A"}</span>
                            <span className="text-gray-500">({c.review_count || 0} reviews)</span>
                          </span>
                          <span className={c.active_jobs > 0 ? "text-emerald-400 font-medium" : "text-gray-500"}>
                            {c.active_jobs || 0} open {c.active_jobs === 1 ? "job" : "jobs"}
                          </span>
                          {c.company_size && (
                            <span className="flex items-center gap-1 text-gray-500"><Users className="w-3 h-3" />{c.company_size}</span>
                          )}
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-gray-600 group-hover:text-crimson flex-shrink-0 mt-2" />
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default CompanyDirectory;
